import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';

import { classNames } from '../../utilities/class-names';

import styles from './NotificationMessage.css';

export class NotificationMessage extends Component {
  constructor() {
    super();

    this.addStickySupport = this.addStickySupport.bind(this);
    this.clearNotificationTimeout = this.clearNotificationTimeout.bind(this);
    this.handleEvent = this.handleEvent.bind(this);
    this.hideNotification = this.hideNotification.bind(this);
    this.setHeight = this.setHeight.bind(this);
    this.setPosition = this.setPosition.bind(this);

    this.state = {
      notificationHeight: 0
    };

    this.notificationMessage = createRef();
    this.notificationMessageText = createRef();

    this.resizeEventRunning = false;
    this.scrollEventRunning = false;
    this.timeoutId = null;
  }

  componentDidMount() {
    const notificationPosition = this.notificationMessage.current.getBoundingClientRect();

    this.notificationMessageTop = notificationPosition.top + document.documentElement.scrollTop;

    this.setState(
      {
        isFullWidth: notificationPosition.width === document.documentElement.clientWidth
      },
      this.addStickySupport
    );

    this.setHeight();
    window.addEventListener('resize', this.handleEvent);
  }

  componentDidUpdate(prevProps) {
    const { message, type } = this.props;

    if (prevProps.message !== message) {
      this.setHeight();
      this.clearNotificationTimeout();

      if (type === 'success') {
        this.timeoutId = window.setTimeout(this.hideNotification, 7000);
      }
    }
  }

  componentWillUnmount() {
    ['resize', 'scroll'].forEach(eventType =>
      window.removeEventListener(eventType, this.handleEvent)
    );
  }

  addStickySupport() {
    const { isFullWidth } = this.state;

    const needsStickySupport = (() => {
      // Detect whether `position: sticky` is supported. It should be set in the CSS for
      // `.notification-message.is-full-width`, so we can check if the browser understands it and if
      // not, add a fallback.
      const notificationMessagePosition = window.getComputedStyle(this.notificationMessage.current)
        .position;

      return notificationMessagePosition.indexOf('sticky') === -1 && isFullWidth;
    })();

    // Only add this for browsers that don't support `position: sticky`.
    if (needsStickySupport) {
      window.addEventListener('scroll', this.handleEvent);
    }
  }

  clearNotificationTimeout() {
    window.clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  handleEvent({ type }) {
    if (!this[`${type}EventRunning`]) {
      this[`${type}EventRunning`] = true;

      const eventCallback = () => {
        if (type === 'scroll') {
          this.setPosition();
        } else {
          this.setHeight();
        }

        this[`${type}EventRunning`] = false;
      };

      // Use `requestAnimationFrame` for throttling
      window.requestAnimationFrame(eventCallback);
    }
  }

  hideNotification() {
    const { handleNotification } = this.props;

    this.setState({ notificationHeight: 0 });

    window.setTimeout(
      handleNotification,
      parseFloat(window.getComputedStyle(this.notificationMessage.current).transitionDuration) *
        1000
    );
  }

  setHeight() {
    this.setState({
      notificationHeight: this.notificationMessageText.current
        ? `${this.notificationMessageText.current.offsetHeight / 10}rem`
        : 0
    });
  }

  setPosition() {
    const { isSticky } = this.state;

    if (window.pageYOffset >= this.notificationMessageTop && !isSticky) {
      this.setState({ isSticky: true });
    }

    if (window.pageYOffset < this.notificationMessageTop && isSticky) {
      this.setState({ isSticky: false });
    }
  }

  render() {
    const { fixedOffset, message, type } = this.props;
    const { isFullWidth, isSticky, notificationHeight } = this.state;

    return (
      <div
        className={classNames(
          styles.notificationMessage,
          isFullWidth && styles.isFullWidth,
          isSticky && styles.isSticky,
          type && styles[`is-${type}`]
        )}
        ref={this.notificationMessage}
        role="alert"
        style={{ height: notificationHeight, top: fixedOffset }}>
        {message && (
          <span
            className={classNames(
              styles.notificationMessageText,
              isFullWidth && styles.isFullWidth,
              type && styles[`is-${type}`]
            )}
            ref={this.notificationMessageText}>
            {message}
          </span>
        )}
      </div>
    );
  }
}

NotificationMessage.propTypes = {
  /**
    The offset for the notification when it sticks to the top of the screen on scroll, e.g. '2rem'.
  */
  fixedOffset: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  handleNotification: (props, propName, componentName) => {
    const { [propName]: prop, type } = props;

    if (type === 'success' && !prop) {
      return new Error(
        `The prop \`${propName}\` is marked as required in \`${componentName}\` if the ` +
          '`type` prop is equal to `success`, but its value is `undefined`.'
      );
    }

    if (typeof prop !== 'undefined' && typeof prop !== 'function') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof prop}\` supplied to ` +
          `\`${componentName}\`, expected \`function\`.`
      );
    }

    return null;
  },
  message: PropTypes.node,
  type: PropTypes.oneOf(['failure', 'information', 'success', 'warning'])
};

NotificationMessage.defaultProps = {
  fixedOffset: null,
  message: null,
  type: null
};
